import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { recordSignIn } from "@/lib/audit.functions";
import { setQueryScope } from "@/lib/query-scope";

/** Log a sign-in to the account activity trail once per browser session. */
function logSignInOnce(userId: string, method: string) {
  const key = `pyng-signin-logged:${userId}`;
  try {
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
  } catch {
    return;
  }
  void recordSignIn({ data: { method } }).catch(() => {});
}

type AuthCtx = {
  user: User | null;
  session: Session | null;
  loading: boolean;
  confirmAuth: () => Promise<User | null>;
  signOut: () => Promise<void>;
};

const Ctx = createContext<AuthCtx>({
  user: null,
  session: null,
  loading: true,
  confirmAuth: async () => null,
  signOut: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  /**
   * Whenever the signed-in identity changes (sign-in, sign-out, account swap)
   * re-scope and wipe the query cache synchronously, so the next account can
   * never paint a frame of the previous account's data.
   */
  const applyIdentity = (nextUserId: string | null) => {
    if (setQueryScope(nextUserId)) queryClient.clear();
  };

  useEffect(() => {
    let active = true;
    let authEventSeen = false;
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      authEventSeen = true;
      applyIdentity(s?.user?.id ?? null);
      setSession(s);
      setLoading(false);
      if (_event === "SIGNED_IN" && s?.user) {
        logSignInOnce(s.user.id, s.user.app_metadata?.provider ?? "password");
      }
    });

    void (async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!active || authEventSeen) return;

      if (!sessionData.session) {
        applyIdentity(null);
        setSession(null);
        setLoading(false);
        return;
      }

      const { data: userData, error } = await supabase.auth.getUser();
      if (!active || authEventSeen) return;
      const ok = !error && !!userData.user;
      applyIdentity(ok ? userData.user!.id : null);
      setSession(ok ? sessionData.session : null);
      setLoading(false);
    })();

    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  return (
    <Ctx.Provider
      value={{
        user: session?.user ?? null,
        session,
        loading,
        confirmAuth: async () => {
          const { data: userData, error: userError } = await supabase.auth.getUser();
          if (userError || !userData.user) return null;

          const { data: sessionData } = await supabase.auth.getSession();
          if (!sessionData.session) return null;

          applyIdentity(userData.user.id);
          setSession(sessionData.session);
          setLoading(false);
          return userData.user;
        },
        signOut: async () => {
          await supabase.auth.signOut();
          applyIdentity(null);
          queryClient.clear();
        },
      }}
    >
      {children}
    </Ctx.Provider>
  );
}

export const useAuth = () => useContext(Ctx);